import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import { socketService } from '@/services/socketService'

export function useConversation(conversationId?: string) {
  const queryClient = useQueryClient()
  const [isConnected, setIsConnected] = useState(false)

  // Fetch conversation data
  const { data: conversation, isLoading, error } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationId ? apiService.getConversation(conversationId) : null,
    enabled: !!conversationId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })

  // Create new conversation
  const createConversationMutation = useMutation({
    mutationFn: (conversationalMode: boolean) => 
      apiService.createConversation(conversationalMode),
    onSuccess: (data) => {
      queryClient.setQueryData(['conversation', data.id], data)
    },
  })

  // Send message
  const sendMessageMutation = useMutation({
    mutationFn: ({ conversationId, message }: { conversationId: string; message: string }) =>
      apiService.sendMessage(conversationId, message),
    onSuccess: (data, variables) => {
      console.log('Message sent, response:', data)
      
      // Add the response to the conversation messages
      if (data && data.response) {
        queryClient.setQueryData(['conversation', variables.conversationId], (old: any) => {
          if (!old) return old
          
          // Find if user message already exists (from optimistic update)
          const existingMessages = old.messages || []
          const lastMessage = existingMessages[existingMessages.length - 1]
          const hasUserMessage = lastMessage && lastMessage.role === 'user'
          
          // Build new messages array
          const newMessages = hasUserMessage 
            ? [...existingMessages] // User message already added optimistically
            : [...existingMessages] // Should not happen, but handle gracefully
          
          // Add assistant's response
          newMessages.push({
            id: `assistant-${Date.now()}`,
            role: 'assistant' as const,
            content: data.response,
            timestamp: new Date().toISOString()
          })
          
          console.log('Updating conversation with:', {
            stage: data.stage,
            progress: data.progress,
            bpmnLength: data.bpmn?.length,
            messageCount: newMessages.length
          })
          
          return {
            ...old,
            messages: newMessages,
            stage: data.stage || old.stage,
            progress: data.progress || old.progress,
            bpmn: data.bpmn || old.bpmn
          }
        })
      }
    },
  })

  // Setup WebSocket connection
  useEffect(() => {
    if (!conversationId) return

    // Connect to WebSocket if not already connected
    if (!socketService.isConnected()) {
      socketService.connect()
    }

    // Subscribe to conversation updates
    const handleConnected = () => {
      setIsConnected(true)
      socketService.subscribeToConversation(conversationId)
    }

    const handleDisconnected = () => {
      setIsConnected(false)
    }

    const handleConversationUpdate = (data: any) => {
      if (data.conversationId === conversationId) {
        console.log('WebSocket conversation update received, NOT invalidating to preserve messages')
        // DON'T invalidate - this causes messages to be lost
        // queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
      }
    }

    const handleBPMNUpdate = (data: any) => {
      if (data.conversationId === conversationId) {
        // Update BPMN data
        queryClient.setQueryData(['conversation', conversationId], (old: any) => ({
          ...old,
          bpmn: data.bpmn,
          stage: data.stage,
          progress: data.progress,
        }))
      }
    }

    const handleStageTransition = (data: any) => {
      if (data.conversationId === conversationId) {
        queryClient.setQueryData(['conversation', conversationId], (old: any) => ({
          ...old,
          stage: data.toStage,
          progress: data.progress,
        }))
      }
    }

    // Register event handlers
    socketService.on('connected', handleConnected)
    socketService.on('disconnected', handleDisconnected)
    socketService.on('conversation:update', handleConversationUpdate)
    socketService.on('bpmn:update', handleBPMNUpdate)
    socketService.on('stage:transition', handleStageTransition)

    // Initial connection
    if (socketService.isConnected()) {
      handleConnected()
    }

    // Cleanup
    return () => {
      socketService.unsubscribeFromConversation(conversationId)
      socketService.off('connected', handleConnected)
      socketService.off('disconnected', handleDisconnected)
      socketService.off('conversation:update', handleConversationUpdate)
      socketService.off('bpmn:update', handleBPMNUpdate)
      socketService.off('stage:transition', handleStageTransition)
    }
  }, [conversationId, queryClient])

  const sendMessage = useCallback(
    async (message: string, overrideConversationId?: string) => {
      const targetConversationId = overrideConversationId || conversationId
      if (!targetConversationId) return
      
      // Optimistically add user's message to the conversation
      const userMessageId = `user-${Date.now()}`
      queryClient.setQueryData(['conversation', targetConversationId], (old: any) => {
        if (!old) {
          // Create a new conversation structure if it doesn't exist
          old = {
            id: targetConversationId,
            messages: [],
            stage: 'initial',
            progress: 0
          }
        }
        
        const newMessages = [
          ...(old.messages || []),
          {
            id: userMessageId,
            role: 'user' as const,
            content: message,
            timestamp: new Date().toISOString()
          }
        ]
        
        return {
          ...old,
          messages: newMessages
        }
      })
      
      await sendMessageMutation.mutateAsync({ conversationId: targetConversationId, message })
    },
    [conversationId, sendMessageMutation, queryClient]
  )

  const createConversation = useCallback(
    async (conversationalMode: boolean = true) => {
      const result = await createConversationMutation.mutateAsync(conversationalMode)
      return result
    },
    [createConversationMutation]
  )

  return {
    conversation,
    isLoading,
    error,
    isConnected,
    sendMessage,
    createConversation,
    isSending: sendMessageMutation.isPending,
    isCreating: createConversationMutation.isPending,
  }
}